import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useCallback, useEffect, useState } from "react";
import {
  adminService,
  type OrderEmailSource,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const SOURCES: { key: OrderEmailSource; label: string }[] = [
  { key: "india-purchase", label: "India" },
  { key: "usa-purchase", label: "USA" },
  { key: "reuse-recycle", label: "Reuse / Recycle" },
];

// Placeholders available in subject + body (Django template syntax).
const SCALAR_PLACEHOLDERS = [
  "{{ customer_name }}",
  "{{ order_number }}",
  "{{ order_date }}",
  "{{ order_status_display }}",
  "{{ payment_method_display }}",
  "{{ currency_symbol }}",
  "{{ subtotal }}",
  "{{ shipping_cost }}",
  "{{ donation_amount }}",
  "{{ total }}",
  "{{ shipping_address.fullName }}",
  "{{ shipping_address.addressLine1 }}",
  "{{ shipping_address.city }}",
  "{{ shipping_address.state }}",
  "{{ shipping_address.postalCode }}",
  "{{ shipping_address.country }}",
  "{{ shipping_address.email }}",
  "{{ shipping_address.phone }}",
];

const ITEMS_LOOP_SNIPPET = `{% for item in items %}
  {{ item.title }} — {{ item.quantity }} × {{ currency_symbol }}{{ item.price }} = {{ currency_symbol }}{{ item.total }}
{% endfor %}`;

export default function OrderEmailTemplates() {
  const [source, setSource] = useState<OrderEmailSource>("india-purchase");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async (src: OrderEmailSource) => {
    try {
      setLoading(true);
      const t = await adminService.getOrderEmailTemplate(src);
      setSubject(t.subject ?? "");
      setBodyHtml(t.body_html ?? "");
      setIsActive(t.is_active);
      setDirty(false);
    } catch {
      toast.error(
        "Could not load template. Make sure the backend migrations have run."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(source);
  }, [source, load]);

  const switchSource = (src: OrderEmailSource) => {
    if (src === source) {
      return;
    }
    if (dirty && !window.confirm("Discard unsaved changes to this template?")) {
      return;
    }
    setSource(src);
  };

  const save = async () => {
    try {
      setSaving(true);
      await adminService.updateOrderEmailTemplate(source, {
        subject: subject.trim(),
        body_html: bodyHtml,
        is_active: isActive,
      });
      toast.success("Email template saved.");
      setDirty(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const copyPlaceholder = (value: string) => {
    void navigator.clipboard?.writeText(value);
    toast.success(`Copied ${value}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-5 md:space-y-6">
        <div>
          <h2 className="text-xl font-bold text-foreground md:text-3xl">
            Order Confirmation Emails
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Separate HTML email templates for each storefront.
          </p>
        </div>

        {/* Source selector */}
        <div className="flex flex-wrap gap-2">
          {SOURCES.map(s => (
            <button
              key={s.key}
              type="button"
              onClick={() => switchSource(s.key)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                source === s.key
                  ? "border-[#654321] bg-[#654321] text-white"
                  : "border-border bg-card hover:bg-muted"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <Switch
                id="active"
                checked={isActive}
                onCheckedChange={v => {
                  setIsActive(v);
                  setDirty(true);
                }}
              />
              <Label htmlFor="active">
                Active (use this template instead of the built-in default)
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" required>
                Subject
              </Label>
              <Input
                id="subject"
                value={subject}
                onChange={e => {
                  setSubject(e.target.value);
                  setDirty(true);
                }}
                placeholder="Order Confirmation - {{ order_number }}"
              />
            </div>

            {/* Editor + preview */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="body">HTML body</Label>
                <textarea
                  id="body"
                  value={bodyHtml}
                  spellCheck={false}
                  onChange={e => {
                    setBodyHtml(e.target.value);
                    setDirty(true);
                  }}
                  className="min-h-[460px] w-full rounded-md border border-border bg-card p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#654321]/40"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Live preview{" "}
                  <span className="font-normal text-muted-foreground">
                    (placeholders shown literally)
                  </span>
                </Label>
                <iframe
                  title="Email preview"
                  srcDoc={bodyHtml}
                  className="min-h-[460px] w-full rounded-md border border-border bg-white"
                  sandbox=""
                />
              </div>
            </div>

            {/* Placeholder reference */}
            <div className="rounded-md border border-border bg-muted/30 p-4">
              <div className="mb-2 text-sm font-semibold">
                Available placeholders (click to copy)
              </div>
              <div className="flex flex-wrap gap-2">
                {SCALAR_PLACEHOLDERS.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => copyPlaceholder(p)}
                    className="rounded border border-border bg-card px-2 py-1 font-mono text-[11px] hover:bg-muted"
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="mt-3 text-sm font-semibold">Order items loop</div>
              <pre className="mt-1 overflow-x-auto rounded border border-border bg-card p-3 font-mono text-[11px] leading-relaxed">
                {ITEMS_LOOP_SNIPPET}
              </pre>
            </div>

            <Button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="bg-[#654321] text-white hover:bg-[#543210]"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save{dirty ? " *" : ""}
            </Button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
